// assets/accessibility.js - Perfect Locks site-wide accessibility helpers.
// PL 2026-11-02: slimmed from 32 KB to the parts nothing else covers.
//   1. Screen reader announcements (live regions #accessibility-announcer-1/-2; theme.js also uses
//      region 1 for "Code copied").
//   2. Cart announcements: "X added to cart. Cart has N items", "Cart updated", "Your cart is now
//      empty". Listens to the theme's own cart events (variant:added, line-item-quantity:change:end,
//      cart:updated) instead of wrapping window.fetch.
//   3. Cart drawer: if focus is lost when the drawer closes, it goes back to what opened it, or to
//      the header cart link. The cart count badge is hidden from screen readers (the cart link
//      already says "Cart, N items").
//   4. Klaviyo forms: missing labels added, empty listboxes lose role="listbox" until they have
//      options (WCAG 1.3.1, 4.1.2). Runs when Klaviyo says a form opened, not on every DOM change.
//   5. Tracking pixels (1x1 images) get alt="" so screen readers skip them (WCAG 1.1.1).
//   7. Desktop mega menus close on Escape when opened by hover (WCAG 1.4.13). PL 2026-11-03.
// Removed: all gallery, thumbnail, dot and progress bar keyboard code
// (the gallery is pl-snap-gallery.js + pl-product-media.js now, which handle keyboard and
// announcements), the window.fetch wrapper and the page-wide MutationObserver.
(function () {
  'use strict';

  // 1. Announcer: two regions, alternated, so the same message twice is still read.
  var regions = null;
  var turn = 0;
  function makeRegion(id) {
    var el = document.getElementById(id);
    if (el) { return el; }
    el = document.createElement('div');
    el.id = id;
    el.className = 'sr-only';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'assertive');
    el.setAttribute('aria-atomic', 'true');
    document.body.insertBefore(el, document.body.firstChild);
    return el;
  }
  function announce(message) {
    if (!regions) { regions = [makeRegion('accessibility-announcer-1'), makeRegion('accessibility-announcer-2')]; }
    regions[0].textContent = '';
    regions[1].textContent = '';
    var region = regions[turn];
    turn = 1 - turn;
    setTimeout(function () { region.textContent = message; }, 50);
  }

  // 2. Cart announcements.
  function items(n) { return n + ' ' + (n === 1 ? 'item' : 'items'); }
  var lastCount = null;
  var added = null;
  var removedLine = false;
  function initCart() {
    var badge = document.querySelector('cart-count, .cart-count, [data-cart-count]');
    if (badge) { lastCount = parseInt(badge.textContent, 10) || 0; }

    document.addEventListener('variant:added', function (e) {
      var line = (e.detail && e.detail.variant) || {};
      added = { title: line.product_title || line.title || 'Item', qty: line.quantity || 1 };
    });
    document.addEventListener('line-item-quantity:change:end', function (e) {
      removedLine = !!(e.detail && e.detail.newLineQuantity === 0);
    });
    document.documentElement.addEventListener('cart:updated', function (e) {
      var cart = e.detail && e.detail.cart;
      if (!cart || typeof cart.item_count !== 'number') { return; }
      var count = cart.item_count;
      var message = '';
      if (added) {
        message = (added.qty > 1 ? added.qty + ' ' : '') + added.title + ' added to cart. Cart has ' + items(count);
      } else if (count !== lastCount) {
        if (count === 0) { message = 'Your cart is now empty'; }
        else if (removedLine) { message = 'Item removed from cart. ' + items(count) + ' remaining'; }
        else { message = 'Cart updated. ' + items(count) + ' in cart'; }
      }
      added = null;
      removedLine = false;
      lastCount = count;
      if (message) { setTimeout(function () { announce(message); }, 300); }
    });

    document.querySelectorAll('.cart-count, [data-cart-count], .header__cart-count').forEach(function (el) {
      el.setAttribute('aria-hidden', 'true');
    });
  }

  // 3. Cart drawer focus return (Focal's focus trap normally does this; this only steps in when focus
  //    ends up on the page body or inside the closed drawer).
  function initDrawer() {
    var drawer = document.getElementById('mini-cart');
    if (!drawer || !window.MutationObserver) { return; }
    var opener = null;
    document.addEventListener('click', function (e) {
      var el = e.target.closest && e.target.closest('[aria-controls="mini-cart"], button[type="submit"], .product-form__add-button');
      if (el && !drawer.contains(el)) { opener = el; }
    }, true);
    new MutationObserver(function () {
      if (drawer.hasAttribute('open')) { return; }
      setTimeout(function () {
        var active = document.activeElement;
        if (active && active !== document.body && !drawer.contains(active)) { return; }
        var targets = [opener, document.querySelector('[aria-controls="mini-cart"]')];
        for (var i = 0; i < targets.length; i++) {
          var el = targets[i];
          if (!el || !el.isConnected || drawer.contains(el) || !el.getClientRects().length) { continue; }
          el.focus();
          if (document.activeElement === el) { return; }
        }
      }, 100);
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  // 4. Klaviyo forms.
  function srLabel(id, text, before) {
    var label = document.createElement('span');
    label.id = id;
    label.className = 'sr-only';
    label.textContent = text;
    before.parentNode.insertBefore(label, before);
  }
  function syncListbox(listbox) {
    var hasOptions = !!listbox.querySelector('[role="option"]');
    if (hasOptions && listbox.hasAttribute('data-original-role')) {
      listbox.setAttribute('role', 'listbox');
    } else if (!hasOptions && listbox.getAttribute('role') === 'listbox') {
      listbox.setAttribute('data-original-role', 'listbox');
      listbox.removeAttribute('role');
    }
  }
  function fixKlaviyo() {
    document.querySelectorAll('[class*="klaviyo-form"]').forEach(function (form) {
      form.querySelectorAll('input[role="combobox"][aria-labelledby]').forEach(function (input) {
        var id = input.getAttribute('aria-labelledby');
        if (id && !document.getElementById(id)) {
          var box = input.closest('[data-testid="form-component"]') || input;
          srLabel(id, input.getAttribute('aria-label') || input.getAttribute('placeholder') || 'Select an option', box.firstChild || box);
        }
      });
      form.querySelectorAll('[role="listbox"], [data-original-role="listbox"]').forEach(function (listbox) {
        var id = listbox.getAttribute('aria-labelledby');
        if (id && !document.getElementById(id) && listbox.id) {
          var input = form.querySelector('input[aria-controls="' + listbox.id + '"]');
          if (input) { srLabel(id, input.getAttribute('aria-label') || input.getAttribute('placeholder') || 'Options', listbox); }
        }
        syncListbox(listbox);
        if (!listbox.hasAttribute('data-a11y-observed') && window.MutationObserver) {
          listbox.setAttribute('data-a11y-observed', 'true');
          new MutationObserver(function () { syncListbox(listbox); }).observe(listbox, { childList: true, subtree: true });
        }
      });
    });
  }
  function initKlaviyo() {
    window.addEventListener('klaviyoForms', function () { setTimeout(fixKlaviyo, 300); });
    fixKlaviyo();
  }

  // 5. Tracking pixels.
  function fixPixels() {
    document.querySelectorAll('img[width="1"][height="1"]:not([alt]), img[id^="_pxPixel"]:not([alt]), img[src*="facebook.com/tr"]:not([alt]), img[src*="trkn.us"]:not([alt])').forEach(function (img) {
      img.setAttribute('alt', '');
      img.setAttribute('aria-hidden', 'true');
    });
  }

  // The aa.trkn.us tracker keeps adding 1x1 images to <body> after load (30+ on a product page), so
  // watch <body>'s direct children only (cheap: not the whole page) and hide new pixels as they land.
  function watchPixels() {
    if (!window.MutationObserver) { return; }
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].tagName === 'IMG') { fixPixels(); return; }
        }
      }
    }).observe(document.body, { childList: true });
  }

  // 6. Theme bug patch (kept): Focal's ProductMedia still runs _onFlickityChanged on every slide change
  //    (the snap gallery fires the same events) and calls firstElementChild.pause() on video slides,
  //    whose first child is our .product__media-video-container, which has no pause(). Without this,
  //    each swipe past a video logs "pause is not a function".
  function patchVideoPause() {
    var VIDEO_SLIDES = '.product__media-item[data-media-type="video"], .product__media-item[data-media-type="external_video"], .product__media-item[data-media-type="model"]';
    function shim(root) {
      if (!root || !root.querySelectorAll) { return; }
      root.querySelectorAll(VIDEO_SLIDES).forEach(function (item) {
        var first = item.firstElementChild;
        if (!first || typeof first.pause === 'function') { return; }
        first.pause = function () {
          if (item.classList.contains('is-selected')) { return; }
          first.querySelectorAll('native-video, external-video, video').forEach(function (player) {
            if (typeof player.pause === 'function') { try { player.pause(); } catch (e) {} }
          });
        };
      });
    }
    shim(document);
    document.addEventListener('flickity:slide-changed', function (e) { shim(e.target); }, true);
    if (window.customElements) {
      customElements.whenDefined('product-media').then(function () {
        var Cls = customElements.get('product-media');
        if (!Cls || !Cls.prototype._onFlickityChanged) { return; }
        var original = Cls.prototype._onFlickityChanged;
        Cls.prototype._onFlickityChanged = function () {
          shim(this);
          try {
            var result = original.apply(this, arguments);
            if (result && typeof result.catch === 'function') { result.catch(function () {}); }
            return result;
          } catch (e) {}
        };
      }).catch(function () {});
    }
  }

  // 7. Desktop mega menus: Escape closes a menu that was opened by hovering (WCAG 1.4.13, content on
  //    hover must be dismissible without moving the pointer). PL 2026-11-03.
  //    theme.js already handles Escape when focus is inside the menu (keyboard use), but it listens on
  //    the navigation only, so a menu opened by hover, with focus elsewhere, ignored Escape. This
  //    listens on the whole page and steps aside when the theme's handler has already acted (it stops
  //    the event) or another dialog used Escape (defaultPrevented). Focus goes back to the menu link
  //    only if it was inside the menu. The menu stays closed until the pointer leaves and comes back,
  //    because the theme opens on mouseenter.
  function initMenuEscape() {
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || e.defaultPrevented) { return; }
      var nav = document.querySelector('desktop-navigation');
      if (!nav || typeof nav.closeDropdown !== 'function') { return; }
      Array.prototype.forEach.call(nav.querySelectorAll('.has-dropdown'), function (item) {
        var link = item.querySelector('[aria-controls]');
        if (!link || link.getAttribute('aria-expanded') !== 'true') { return; }
        var hadFocus = item.contains(document.activeElement);
        nav.closeDropdown(item);
        if (hadFocus) { link.focus(); }
      });
    });
  }

  function init() {
    patchVideoPause();
    initCart();
    initDrawer();
    initKlaviyo();
    fixPixels();
    watchPixels();
    initMenuEscape();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Pixels and Klaviyo forms are injected by apps after load.
  window.addEventListener('load', function () {
    setTimeout(function () { fixPixels(); fixKlaviyo(); }, 1500);
  });

  window.AccessibilityAnnouncer = { announce: announce };
})();
